import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../shared/interfaces/api';
import { MayBe } from '../../shared/services/network-status.service';

const BASE_URL = environment.apiUrl;

function mockTransactionResponse(): ApiResponse<any> {
  return {
    message: 'Mock transaction loaded',
    data: {
      token: 'mock-token',
      invoice_id: 'INV-MOCK-0001',
      amount: 200,
      createdAt: new Date().toISOString(),
      payment: {
        id: 'mock-method-id',
        method: 'BKASH',
        type: 'personal',
        color_code: '#e2136e',
        logo: '/icons/pay-service.svg',
      },
    },
  };
}

function mockAccountNumberResponse(): ApiResponse<any> {
  return {
    message: 'Mock account number loaded',
    data: {
      privacy_setting: true,
      phone_number: '01712345678',
    },
  };
}

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private http = inject(HttpClient);

  public token = signal<string>('');

  public expiryTime = signal<MayBe<number>>(null);

  private tick = signal<number>(Date.now());

  public countdownDisplay = computed(() => {
    const expiry = this.expiryTime();
    const now = this.tick();

    if (!expiry) return '00:00';

    const diff = expiry - now;
    if (diff <= 0) return 'Payment Expired';

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  });

  constructor() {
    setInterval(() => this.tick.set(Date.now()), 1000);
  }

  public getPaymentMethods(transactionId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${BASE_URL}/methods`, {
      headers: this.getHeaders(),
      params: { transactionId },
    });
  }

  public getTransactionById(transactionId: string): Observable<ApiResponse<any>> {
    const request$ = environment.useMockData
      ? of(mockTransactionResponse())
      : this.http.get<ApiResponse<any>>(`${BASE_URL}/transaction/${transactionId}`);

    return request$.pipe(
      tap((res) => {
        const expiry = new Date(res.data.createdAt).getTime() + 15 * 60 * 1000; //? 15 minutes
        this.expiryTime.set(expiry);
      }),
    );
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders().set('Sbpay-api-key', this.getToken());
  }

  public getMethodById(methodId: string, transactionId: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${BASE_URL}/methods/${methodId}`, {
      headers: this.getHeaders(),
      params: { transactionId },
    });
  }

  public updatePaymentTransaction(
    transactionId: string,
    payload: any,
  ): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(
      `${BASE_URL}/transaction/update-payment/${transactionId}`,
      payload,
      {
        headers: this.getHeaders(),
      },
    );
  }

  public getAccountNumber(
    method_id: string,
    payment_type: string,
    transactionId: string,
  ): Observable<ApiResponse<any>> {
    if (environment.useMockData) {
      return of(mockAccountNumberResponse());
    }

    const params = {
      method_id,
      payment_type,
      transactionId,
    };
    return this.http.get<ApiResponse<any>>(`${BASE_URL}/methods/fetch-account-no`, {
      params,
      headers: this.getHeaders(),
    });
  }

  public verifyTransaction(transactionId: string, payload: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${BASE_URL}/payment-verify/${transactionId}`,
      payload,
      {
        headers: this.getHeaders(),
      },
    );
  }

  public setToken(value: string) {
    this.token.set(value);
  }

  public getToken() {
    return this.token();
  }
}
